// UnitRuleStep.js
//
// The steps of the unit view for the table's ingestion rules (Spec 2 sections 4.3, 4.5 and
// 6.4): one per mutating rule, which changes cells ("Rule: Tidy insured names"), and one per
// skip rule, which leaves rows out ("Rule: Skip rows with no premium"). Each shows the rows
// its last run touched and lets the person keep chosen rows as they were.
//
// The rule's record on the unit carries its summary:
//
//     summary = { count, rows: [the first five entries], pages: { prefix, count } | null,
//                 fingerprint, not_applied }
//     entry   = { key: { row: 14 }, identifier, period, read: { column: value },
//                 wrote: { column: { before, after } } (a mutating rule only),
//                 undone: true (only when a person kept the row as it was) }
//
// When the rule touched more than five rows, every entry is stored, in order, in chunks of
// at most 10,000 under the unit's own process_summaries folder ({prefix}000000.json, ...).
// "Review all" reads exactly one chunk at a time, and moving to another releases the one
// before. A chunk is bound to the unit, the rule, the summary's version and its page; a
// response for any other is ignored.
//
// The person's choices are a draft of row keys alone ({ key, undone } per changed row), so
// it survives moving between chunks without keeping their rows. A choice on a document row
// is written referring to the unit's base, which a refill replaces. It belongs to the summary
// version it was made against: when Decode runs the rule again the summary is a new version,
// the rows the choices were made on may have changed, and the draft is cleared with a notice.
// While the summary is the same, a draft held when the table stopped being editable is set
// aside until the person restores or discards it (UnitStepFrame's useStepDraft).

import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';
import UnitStepFrame, {
  ApplyBar,
  SetAsideNotice,
  cellText,
  isInFolder,
  processSummaryFolder,
  rowKeyId,
  targetLabel,
  useStepDraft,
} from './UnitStepFrame';
import { activeDecisions, activeValue, rowKeyText } from '../../../utils/decisions';
import { editUnitDecisions, requestUnit } from '../../../services/cleaningGestures';
import { fetchGcsBlobByPrefix } from '../../../services/api_utils';
import { formatPeriodKey, getPeriodType } from '../../../utils/periodUtils';
import { countNoun } from '../../../utils/pluralise';
import { isRawTable } from '../../../utils/tableTypes';

// The backend's RULE_SUMMARY_PAGE_ROWS: the rows in one stored chunk.
export const SUMMARY_CHUNK_ROWS = 10000;
// The rows of a loaded chunk drawn at once.
const DISPLAY_ROWS = 100;

const DEFINITION = 'definition';
const RERUN_NOTICE = 'Decode ran this rule again, so the choices you had not applied were cleared. Choose again.';

// What a mutating rule does, from its definition, for a rule saved without a description.
const ACTION_SENTENCES = {
  trim: (column) => `Trims the spaces around ${column}.`,
  remove_spaces: (column) => `Removes the spaces in ${column}.`,
  set_value: (column, action) => (action.value
    ? `Sets ${column} to '${action.value}'.`
    : `Sets ${column}.`),
  concatenate: (column) => `Joins values into ${column}.`,
  transform_value: (column) => `Changes the values of ${column}.`,
};

// Props: number, companyId, tableId, unit, table, rule (the table's rule, which names the
// step), kind ('rule' for a mutating rule, 'skip' for a skip rule), verdicts, editBlock,
// fileDeletionSet (the file's deletion is set: Review all, the controls that move through the
// rows and Retry are disabled, Spec 5 section 5), and the unit view's draftEpoch and
// runUnitChange(send, onApplied).
export default function UnitRuleStep({
  number,
  companyId,
  tableId,
  unit,
  table,
  rule,
  kind,
  verdicts,
  editBlock,
  fileDeletionSet,
  draftEpoch,
  runUnitChange,
}) {
  const processId = `${kind}:${rule.id}`;
  const record = unit.processes?.[processId] || null;
  const summary = record?.summary || null;
  const pages = summary?.pages || null;
  const pagesPrefix = pages?.prefix || '';
  const pageCount = pages?.count || 0;
  // The summary's version: a content-identical summary keeps its fingerprint and chunks.
  const versionKey = summary ? `${summary.fingerprint || ''}|${pagesPrefix}` : '';

  const {
    changes: draft, setAsideChanges, change, clear, restore,
  } = useStepDraft(draftEpoch, { versionKey: '', entries: {} });
  const [reviewPage, setReviewPage] = useState(null);     // the chunk under review, or null
  const [loaded, setLoaded] = useState(null);             // { unitId, processId, versionKey, page, rows }
  const [loadState, setLoadState] = useState('idle');     // 'idle' | 'loading' | 'failed'
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [displayPage, setDisplayPage] = useState(0);
  const [applying, setApplying] = useState(false);

  // The chunk under review, kept within the summary as it now stands.
  const page = reviewPage === null || pageCount === 0 ? null : Math.min(reviewPage, pageCount - 1);

  useEffect(() => {
    // Leaving review, or moving to another chunk, releases the chunk held.
    setLoaded(null);
    if (page === null) {
      setLoadState('idle');
      return undefined;
    }
    const chunkPath = `${pagesPrefix.replace(/\/+$/, '')}/${String(page).padStart(6, '0')}.json`;
    if (!isInFolder(chunkPath, processSummaryFolder(companyId, tableId, unit.id))) {
      setLoadState('failed');
      return undefined;
    }
    let cancelled = false;
    setLoadState('loading');
    // A chunk is a JSON list of entries.
    fetchGcsBlobByPrefix(chunkPath)
      .then((blob) => blob.text())
      .then((text) => {
        const rows = JSON.parse(text);
        if (!Array.isArray(rows)) throw new Error('A rule summary chunk is not a list of rows');
        if (cancelled) return;
        setLoaded({ unitId: unit.id, processId, versionKey, page, rows });
        setDisplayPage(0);
        setLoadState('idle');
      })
      .catch((error) => {
        console.error('Failed to load a rule summary chunk:', error);
        if (!cancelled) setLoadState('failed');
      });
    return () => { cancelled = true; };
  }, [companyId, tableId, unit.id, processId, versionKey, pagesPrefix, page, loadAttempt]);

  // A chunk is shown only while it is the one asked for.
  const chunkRows = loaded
    && loaded.unitId === unit.id
    && loaded.processId === processId
    && loaded.versionKey === versionKey
    && loaded.page === page
    ? loaded.rows
    : null;

  // The draft, set aside or not, belongs to the summary version it was made against. One
  // made against an earlier version is cleared with a notice; one of this version that
  // was set aside waits for Restore or Discard, and the choices cannot change meanwhile.
  const heldDraft = setAsideChanges === null ? draft : setAsideChanges;
  const draftWasCleared = heldDraft.versionKey !== versionKey && Object.keys(heldDraft.entries).length > 0;
  const setAsideCount = setAsideChanges !== null && setAsideChanges.versionKey === versionKey
    ? Object.keys(setAsideChanges.entries).length
    : 0;
  const entries = draft.versionKey === versionKey ? draft.entries : {};
  const changeCount = Object.keys(entries).length;
  const choicesDisabled = Boolean(editBlock) || applying || setAsideCount > 0;

  const definition = (record && activeValue(record, DEFINITION)) || rule;
  // What the rule does: its saved description, or, for a mutating rule saved without one,
  // a sentence from its definition.
  let description = definition.description;
  if (!description && kind === 'skip') {
    description = 'Leaves out the rows this rule matches.';
  } else if (!description) {
    const action = definition.action || {};
    const column = definition.level === 'cell' ? definition.target_column : action.column;
    const sentence = ACTION_SENTENCES[action.type];
    description = sentence && column ? sentence(targetLabel(column), action) : 'Changes the rows this rule matches.';
  }
  const undoneCount = record
    ? activeDecisions(record).filter(({ key, version }) => (
      key !== DEFINITION && version.value?.applied === false
    )).length
    : 0;
  const touchedCount = summary?.count || 0;
  const actedCount = Math.max(touchedCount - undoneCount, 0);
  const showIdentifier = !isRawTable(table);
  const showPeriod = !isRawTable(table);
  const periodType = getPeriodType(table);
  const identifierLabel = targetLabel(table.identifier?.name || 'Identifier');

  const effectiveUndone = (entry) => {
    const id = rowKeyId(entry.key);
    return id in entries ? entries[id].undone : entry.undone === true;
  };

  const toggle = (entry) => {
    const id = rowKeyId(entry.key);
    const undoneInRun = entry.undone === true;
    change((previous) => {
      const next = previous.versionKey === versionKey ? { ...previous.entries } : {};
      const current = id in next ? next[id].undone : undoneInRun;
      const wanted = !current;
      if (wanted === undoneInRun) delete next[id];
      else next[id] = { key: entry.key, undone: wanted };
      return { versionKey, entries: next };
    });
  };

  const apply = async () => {
    const choices = Object.values(entries).map(({ key, undone }) => ({
      key,
      value: { applied: !undone },
      // A document unit's row ({ page, record }) is a row of the base it was read into: the
      // choice names that base, so a refill of the record set drops it with a note
      // (Spec 2 section 1.3). A sheet row's number is the sheet copy's and needs nothing.
      refersTo: unit.source?.record_set_id ? { base: unit.base } : null,
    }));
    setApplying(true);
    await runUnitChange(() => editUnitDecisions(companyId, tableId, unit.id, processId, choices), clear);
    setApplying(false);
  };

  const renderRows = (rows) => (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Row</TableCell>
            {showIdentifier && <TableCell>{identifierLabel}</TableCell>}
            {showPeriod && <TableCell>Period</TableCell>}
            {kind === 'skip' ? (
              <>
                <TableCell>Why</TableCell>
                <TableCell>Keep the row</TableCell>
              </>
            ) : (
              <>
                <TableCell>Was</TableCell>
                <TableCell>Now</TableCell>
                <TableCell>Undo</TableCell>
              </>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((entry) => {
            // What the row's cells are now: as the rule wrote them, as they were when the
            // person keeps the row, or, for a row kept in the last run and about to be
            // changed again, only known once the rule runs on it.
            let now = Object.values(entry.wrote || {}).map((written) => cellText(written.after)).join(', ');
            if (entry.undone) now = <i>changed when applied</i>;
            if (effectiveUndone(entry)) now = <i>unchanged</i>;
            return (
              <TableRow key={rowKeyId(entry.key)}>
                <TableCell>{rowKeyText(entry.key)}</TableCell>
                {showIdentifier && <TableCell>{cellText(entry.identifier)}</TableCell>}
                {showPeriod && <TableCell>{entry.period ? formatPeriodKey(entry.period, periodType) : '—'}</TableCell>}
                {kind === 'skip' ? (
                  <TableCell>
                    {Object.entries(entry.read || {})
                      .map(([column, value]) => `${targetLabel(column)} is ${value === null || value === '' ? 'empty' : `'${value}'`}`)
                      .join(', ')}
                  </TableCell>
                ) : (
                  <>
                    <TableCell>
                      {Object.values(entry.wrote || {}).map((written) => cellText(written.before)).join(', ')}
                    </TableCell>
                    <TableCell>{now}</TableCell>
                  </>
                )}
                <TableCell padding="checkbox">
                  <Checkbox
                    size="small"
                    checked={effectiveUndone(entry)}
                    disabled={choicesDisabled}
                    onChange={() => toggle(entry)}
                    inputProps={{
                      'aria-label': `${kind === 'skip' ? 'Keep' : 'Undo'} ${rowKeyText(entry.key)}`,
                    }}
                  />
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const renderReview = () => {
    const first = page * SUMMARY_CHUNK_ROWS + 1;
    const last = Math.min((page + 1) * SUMMARY_CHUNK_ROWS, touchedCount);
    const displayed = chunkRows
      ? chunkRows.slice(displayPage * DISPLAY_ROWS, (displayPage + 1) * DISPLAY_ROWS)
      : [];
    return (
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
          <Typography variant="body2">
            {`Rows ${first.toLocaleString()}–${last.toLocaleString()} of ${touchedCount.toLocaleString()}`}
          </Typography>
          <Box sx={{ flex: 1 }} />
          <Button
            size="small"
            disabled={fileDeletionSet || page === 0}
            onClick={() => setReviewPage(page - 1)}
            sx={{ textTransform: 'none' }}
          >
            Previous
          </Button>
          <Button
            size="small"
            disabled={fileDeletionSet || page >= pageCount - 1}
            onClick={() => setReviewPage(page + 1)}
            sx={{ textTransform: 'none' }}
          >
            Next
          </Button>
          <Button size="small" disabled={fileDeletionSet} onClick={() => setReviewPage(null)} sx={{ textTransform: 'none' }}>
            Back to the first rows
          </Button>
        </Box>
        {loadState === 'loading' && (
          <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}><CircularProgress size={24} /></Box>
        )}
        {loadState === 'failed' && (
          <Alert
            severity="error"
            action={(
              <Button color="inherit" size="small" disabled={fileDeletionSet} onClick={() => setLoadAttempt((n) => n + 1)}>
                Retry
              </Button>
            )}
          >
            These rows could not be loaded.
          </Alert>
        )}
        {chunkRows && renderRows(displayed)}
        {chunkRows && chunkRows.length > DISPLAY_ROWS && (
          <TablePagination
            component="div"
            count={chunkRows.length}
            page={displayPage}
            rowsPerPage={DISPLAY_ROWS}
            rowsPerPageOptions={[]}
            disabled={fileDeletionSet}
            onPageChange={(event, next) => setDisplayPage(next)}
          />
        )}
      </Box>
    );
  };

  let body;
  if (!record || !summary) {
    body = <Typography variant="body2" color="text.secondary">Not run on this table yet.</Typography>;
  } else if (summary.not_applied) {
    body = (
      <Typography variant="body2" color="text.secondary">
        This rule did not apply: this table has none of the columns it reads.
      </Typography>
    );
  } else {
    body = (
      <>
        <Typography variant="body2" sx={{ mb: 1 }}>
          {kind === 'skip'
            ? `Left out ${countNoun(actedCount, 'row')}${undoneCount ? ` · ${countNoun(undoneCount, 'row')} kept by you` : ''}`
            : `Changed ${countNoun(actedCount, 'row')}${undoneCount ? ` · ${undoneCount} kept as ${undoneCount === 1 ? 'it was' : 'they were'} by you` : ''}`}
        </Typography>
        {page === null && summary.rows.length > 0 && renderRows(summary.rows)}
        {page === null && pages && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
            <Typography variant="caption" color="text.secondary">
              {`Showing the first ${summary.rows.length}.`}
            </Typography>
            <Button size="small" disabled={fileDeletionSet} onClick={() => setReviewPage(0)} sx={{ textTransform: 'none' }}>
              {`Review all ${touchedCount.toLocaleString()}`}
            </Button>
          </Box>
        )}
        {page !== null && renderReview()}
      </>
    );
  }

  return (
    <UnitStepFrame
      number={number}
      title={`Rule: ${rule.name || 'Unnamed rule'}`}
      verdicts={verdicts}
      record={record}
      menu
      editBlock={editBlock}
      onRequest={(requestKind) => runUnitChange(() => requestUnit(companyId, tableId, unit.id, processId, requestKind))}
      defaultOpen={touchedCount > 0 || verdicts.length > 0}
    >
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {description}
      </Typography>
      {draftWasCleared && (
        <Alert severity="info" sx={{ mb: 1 }} onClose={clear}>
          {RERUN_NOTICE}
        </Alert>
      )}
      <SetAsideNotice changeCount={setAsideCount} editBlock={editBlock} onRestore={restore} onDiscard={clear} />
      {body}
      <ApplyBar
        changeCount={changeCount}
        editBlock={editBlock}
        applying={applying}
        onApply={apply}
        onDiscard={clear}
      />
    </UnitStepFrame>
  );
}
